import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import useCapabilities, { useCanViewInteractionsBool } from "@/hooks/useCapabilities";
import { RECENT_INTERACTIONS_QUERY } from "@/pages/home/queries";
import { isAccessibleInteraction } from "@/pages/home/_utils/interactionDisplay";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";

export const INITIAL_VISIBLE_INTERACTION_COUNT = 4;
export const LOAD_MORE_INTERACTION_INCREMENT = 4;
export const MAX_RECENT_INTERACTION_COUNT = 12;

export default function useRecentInteractions() {
  const { id: currentUserId } = useCapabilities();
  const canViewInteractions = useCanViewInteractionsBool();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_INTERACTION_COUNT);

  const { data, previousData, loading, networkStatus, error } = useQuery(RECENT_INTERACTIONS_QUERY, {
    variables: {
      first: MAX_RECENT_INTERACTION_COUNT,
      input: {
        authorId: currentUserId ?? undefined
      },
      orderBy: {
        field: "CREATED_AT",
        direction: "DESC"
      }
    },
    skip: !currentUserId || !canViewInteractions,
    fetchPolicy: "cache-and-network"
  });

  const accessibleInteractions = useMemo(() => {
    const nodes = (data ?? previousData)?.crmInteractions?.nodes ?? [];
    return nodes.filter(isAccessibleInteraction);
  }, [data, previousData]);

  const interactions = useMemo(
    () => accessibleInteractions.slice(0, visibleCount),
    [accessibleInteractions, visibleCount]
  );

  const maxVisibleCount = Math.min(accessibleInteractions.length, MAX_RECENT_INTERACTION_COUNT);
  const hasMore = visibleCount < maxVisibleCount;
  const resolvedData = data ?? previousData;

  return {
    interactions,
    totalCount: accessibleInteractions.length,
    loading: isInitialQueryLoading({
      loading,
      networkStatus,
      itemCount: accessibleInteractions.length,
      hasData: resolvedData !== undefined,
      error
    }),
    canViewInteractions,
    hasMore,
    loadMore: () =>
      setVisibleCount((count) => Math.min(count + LOAD_MORE_INTERACTION_INCREMENT, MAX_RECENT_INTERACTION_COUNT))
  };
}
